import styled, { css, up } from "@xstyled/styled-components";
import { useMedia } from "react-use";
import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

import TopLogos from "./assets/top-logos.svg";
import BottomLogos from "./assets/bottom-logos.svg";
import MobileLogos from "./assets/mobile-logos.svg";
import { ResponsiveButtonContainer } from "./components/ResponsiveButtonContainer";

import { Button, Heading, List, Spacing, Text, theme } from "@otta/design";
import { palette, pxToEm, pxToRem } from "@otta/design-tokens";
import {
  CatalogueProductId,
  Currency,
  CurrentUserDocument,
  ProductQuoteDocument,
} from "@hire/schema";
import { Loading } from "@otta/shared-components";
import { formatCurrency } from "@hire/util/currencies";
import { Link } from "@hire/components/links";
import { LogoHeader } from "@hire/components/layout/QuizLayout";

const PageContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 4rem);
  flex-direction: column;
  padding-bottom: 212;

  ${up(
    "desktop",
    css`
      flex-direction: row;
      padding-bottom: unset;
    `
  )}
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
`;

const ColumnInner = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem 1rem;
  flex-direction: column;
  gap: xl;
  ${up(
    "desktop",
    css`
      max-width: ${pxToRem(400)};
      gap: 40;
      padding-top: 5rem;
    `
  )}
`;

const Main = styled.main`
  background-color: white;
`;

const PaddedContent = styled.div`
  padding: xl lg 0 lg;

  ${up(
    "desktop",
    css`
      padding: 0;
    `
  )}
`;

const PricingInformationHeadingContainer = styled.div`
  padding: xl lg;

  ${up(
    "desktop",
    css`
      background-color: transparent;
      padding: 0 0 16 0;
    `
  )}
`;

const StyledPricingHeading = styled.div`
  align-self: center;
  ${up(
    "desktop",
    css`
      max-width: ${pxToRem(517)};
    `
  )}
`;

const ContinueButton = styled(Button)`
  text-decoration: none;
  width: 100%;
`;

const Footer = styled.div`
  display: flex;
  align-items: baseline;
  gap: sm;
`;

const MobileHeadingCard = styled.div`
  background-color: beige-200;
  background-image: url(${MobileLogos});
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  min-height: ${pxToRem(212)};
  min-width: ${pxToRem(343)};
  padding: 0 41;
  justify-content: center;
  align-items: center;
`;

const PaddedContentSpacer = styled.div`
  ${up(
    "desktop",
    css`
      max-width: ${pxToRem(400)};
    `
  )}
`;

function DesktopPricingCover() {
  return (
    <Column
      style={{
        backgroundColor: palette.beige.shade200,
        justifyContent: "space-between",
      }}
    >
      <img src={TopLogos} alt="Top company logos" />
      <StyledPricingHeading>
        <Spacing>
          <Heading align="center" size={5}>
            You&apos;re in good company
          </Heading>
          <Text align="center" size={4}>
            Here are some of the companies hiring on Welcome to the Jungle
            today.
          </Text>
        </Spacing>
      </StyledPricingHeading>
      <img src={BottomLogos} alt="Bottom company logos" />
    </Column>
  );
}

function MobilePricingCover() {
  return (
    <MobileHeadingCard>
      <StyledPricingHeading>
        <Spacing>
          <Heading align="center" size={1}>
            You&apos;re in good company
          </Heading>
          <Text align="center">
            Here are some of the companies hiring on Welcome to the Jungle
            today.
          </Text>
        </Spacing>
      </StyledPricingHeading>
    </MobileHeadingCard>
  );
}

function PricingInformation({ isDesktop }: { isDesktop: boolean }) {
  const { data } = useQuery(CurrentUserDocument);
  const navigate = useNavigate();
  const loggedIn = !!data?.me;
  const loggedInAsCandidate = data?.me?.__typename === "CurrentCandidate";

  const { data: productQuoteData, loading: productQuoteLoading } = useQuery(
    ProductQuoteDocument,
    {
      variables: {
        quantity: 1,
        productId: CatalogueProductId.JobSlotMonthly,
      },
    }
  );

  function getValueForCore() {
    return productQuoteData?.catalogue?.product?.quote
      ? formatCurrency(
          productQuoteData.catalogue.product.quote.netTotal,
          productQuoteData.catalogue.product.quote.currency
        )
      : formatCurrency("199", Currency.Gbp);
  }

  const formattedCorePriceValue = getValueForCore();

  useEffect(() => {
    if (loggedInAsCandidate) {
      navigate("/candidate-account");
    }
  }, [loggedInAsCandidate, navigate]);

  if (productQuoteLoading) {
    return <Loading />;
  }

  return (
    <Column style={{ width: "100%", alignItems: "center" }}>
      <ColumnInner>
        <div style={{ maxWidth: pxToRem(400) }}>
          <PricingInformationHeadingContainer>
            <Heading
              style={{ maxWidth: pxToRem(400) }}
              size={isDesktop ? 3 : 2}
            >
              For {formattedCorePriceValue} per month, you get:
            </Heading>
          </PricingInformationHeadingContainer>
          <PaddedContent>
            <PaddedContentSpacer>
              <Spacing size={isDesktop ? 5 : 2}>
                <Spacing>
                  <List type="positive" style={{ maxWidth: "343px" }}>
                    <li>
                      <Text inline bold>
                        1 job slot.
                      </Text>{" "}
                      Buy more or cancel anytime.
                    </li>
                    <li>
                      Candidates{" "}
                      <Text inline bold>
                        {" "}
                        twice as likely to progress to interview{" "}
                      </Text>{" "}
                      compared to LinkedIn.
                    </li>
                    <li>
                      <Text inline bold>
                        Diverse candidates.
                      </Text>{" "}
                      53% female or non-binary and 38% from under-represented
                      ethnic groups.
                    </li>
                  </List>
                </Spacing>
                <ResponsiveButtonContainer breakpoint={theme.screens.desktop}>
                  <Spacing size={-1.5}>
                    <ContinueButton
                      data-analytics-id="see-how-my-job-would-perform"
                      to="/quiz/job-performance"
                      level="secondary"
                      as={Link}
                    >
                      See how my job would perform
                    </ContinueButton>
                    <ContinueButton
                      data-analytics-id="continue"
                      onClick={() => {
                        if (!loggedIn) {
                          navigate("/quiz/company");
                        } else {
                          navigate("/quiz/success");
                        }
                      }}
                      level="primary"
                    >
                      Continue
                    </ContinueButton>
                    <Footer>
                      <Text bold>No commitment.</Text>
                      <Text size={-1}>Pay monthly, cancel online anytime.</Text>
                    </Footer>
                  </Spacing>
                </ResponsiveButtonContainer>
              </Spacing>
            </PaddedContentSpacer>
          </PaddedContent>
        </div>
      </ColumnInner>
    </Column>
  );
}

export function PricingDetails() {
  const isDesktop = useMedia(`(min-width: ${pxToEm(theme.screens.desktop)})`);
  return (
    <Main>
      <LogoHeader />
      <PageContainer>
        {isDesktop && <DesktopPricingCover />}
        {!isDesktop && <MobilePricingCover />}
        <PricingInformation isDesktop={isDesktop} />
      </PageContainer>
    </Main>
  );
}
